import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiPlus, FiEdit2, FiSave, FiFileText } from 'react-icons/fi';
import { procesosService } from '../../services';
import { LoadingGrid } from '../../components/UI/Loading';
import './ProcesosList.scss';

/**
 * Vista de lista de procesos con edición inline
 * Basado en el diseño del PDF del cliente (páginas 2-7)
 */
const ProcesosList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const processType = location.state?.processType || 'ENCAPSULATION';

  const [procesos, setProcesos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [generalNotes, setGeneralNotes] = useState('');
  const [newRow, setNewRow] = useState(null);

  useEffect(() => {
    loadProcesos();
  }, [processType]);

  const loadProcesos = async () => {
    setIsLoading(true);
    try {
      const result = await procesosService.getByType(processType, { limit: 100 });
      if (result.success) {
        setProcesos(result.data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getPageTitle = () => {
    const titles = {
      'ENCAPSULATION': 'REGISTROS\nDE ENCAPSULADO',
      'TEA_PACKING': 'REGISTROS\nDE TÉS',
      'PACKAGING': 'REGISTROS\nDE EMPACADO',
      'CLEANING': 'REGISTROS\nDE LIMPIEZA',
      'RECEIVING': 'REGISTROS\nPARA RECIBIR\nPRODUCTOS',
      'RETURNS': 'REGISTROS DE\nRETORNO DE\nPRODUCTOS',
      'MAINTENANCE': 'REGISTRO\nDE MANTENIMIENTO'
    };
    return titles[processType] || 'REGISTROS';
  };

  const handleAddNew = () => {
    const emptyRow = createEmptyRow(processType);
    setNewRow(emptyRow);
    setEditingId('new');
  };

  const handleEdit = (id) => {
    setEditingId(id);
  };

  const handleSave = async (proceso) => {
    try {
      let result;
      if (proceso.id === 'new') {
        // Separar campos según la estructura correcta de la API
        const productionProcess = {
          process_type: processType,
          product_name: proceso.product_name || '',
          process_date: proceso.process_date || new Date().toISOString().split('T')[0],
          operator_name: proceso.operator_name || '',
          notes: generalNotes || ''
        };

        // Agregar batch_number si existe
        if (proceso.batch_number?.trim()) {
          productionProcess.batch_number = proceso.batch_number.trim();
        }

        // Construir process_details según el tipo de proceso
        const processDetails = {};

        switch (processType) {
          case 'TEA_PACKING':
            processDetails.packed_units = parseInt(proceso.packed_units) || 0;
            processDetails.packer_name = proceso.packer_name || '';
            if (proceso.company_name?.trim()) {
              processDetails.company_name = proceso.company_name.trim();
            } else {
              processDetails.company_name = null;
            }
            break;

          case 'PACKAGING':
            processDetails.packed_units = parseInt(proceso.packed_units) || 0;
            processDetails.units_per_container = 1; // Valor fijo en 1
            processDetails.total_containers = 1; // Valor fijo en 1
            processDetails.packer_name = proceso.packer_name || '';
            if (proceso.company_name?.trim()) {
              processDetails.company_name = proceso.company_name.trim();
            } else {
              processDetails.company_name = null;
            }
            break;

          case 'ENCAPSULATION':
            processDetails.net_weight_per_capsule = parseFloat(proceso.net_weight_per_capsule) || 0;
            processDetails.actual_weight_per_capsule = parseFloat(proceso.actual_weight_per_capsule) || 0;
            processDetails.bottles_produced = parseInt(proceso.bottles_produced) || 0;
            break;

          case 'CLEANING':
            processDetails.deep_cleaning = proceso.deep_cleaning || false;
            processDetails.weekly_cleaning = proceso.weekly_cleaning || false;
            processDetails.production_area = proceso.production_area || false;
            processDetails.warehouse_area = proceso.warehouse_area || false;
            processDetails.outside_area = proceso.outside_area || false;
            processDetails.bathroom_area = proceso.bathroom_area || proceso.restroom || false;
            processDetails.cleaner_name = proceso.cleaner_name || proceso.operator_name || '';
            break;

          case 'RECEIVING':
            processDetails.received_boxes = parseInt(proceso.received_boxes) || 0;
            processDetails.weight_per_box = parseFloat(proceso.weight_per_box) || 0;
            processDetails.total_received = parseFloat(proceso.total_received) || 0;
            processDetails.supplier_name = proceso.supplier_name || proceso.company_name || '';
            processDetails.received_by_name = proceso.received_by_name || proceso.receiver_name || '';
            break;

          case 'RETURNS':
            processDetails.returned_quantity = parseInt(proceso.returned_quantity || proceso.quantity_returned) || 0;
            processDetails.client_name = proceso.client_name || '';
            processDetails.return_status = proceso.return_status || 'GOOD';
            break;

          case 'MAINTENANCE':
            processDetails.equipment_name = proceso.equipment_name || '';
            processDetails.maintenance_type = 'PREVENTIVE'; // Valor fijo
            processDetails.technician_name = proceso.technician_name || '';
            processDetails.parts_replaced = 'N/A'; // Valor fijo
            processDetails.total_cost = 0; // Valor fijo
            processDetails.is_operational = true; // Valor fijo
            processDetails.description = proceso.description || proceso.notes || '';
            break;

          default:
            // Para otros tipos, incluir todo excepto campos comunes
            Object.keys(proceso).forEach(key => {
              if (!['id', 'product_name', 'process_date', 'operator_name', 'batch_number'].includes(key)) {
                processDetails[key] = proceso[key];
              }
            });
        }

        result = await procesosService.createProceso({
          process_type: processType,
          production_process: productionProcess,
          process_details: processDetails
        });
        setNewRow(null);
      } else {
        // Usar el production_process_id para actualizar
        const productionProcessId = proceso.production_process_id || proceso.production_process?.id;

        if (!productionProcessId) {
          console.error('No se encontró production_process_id para actualizar', proceso);
          return;
        }

        // Para los endpoints PUT específicos, enviar solo los campos del proceso específico
        let updateData = {};

        switch (processType) {
          case 'TEA_PACKING':
            updateData = {
              packed_units: parseInt(proceso.packed_units) || 0,
              packer_name: proceso.packer_name || ''
            };
            if (proceso.company_name?.trim()) {
              updateData.company_name = proceso.company_name.trim();
            }
            result = await procesosService.updateTeaPacking(productionProcessId, updateData);
            break;

          case 'PACKAGING':
            updateData = {
              packed_units: parseInt(proceso.packed_units) || 0,
              units_per_container: 1,
              total_containers: 1,
              packer_name: proceso.packer_name || ''
            };
            if (proceso.company_name?.trim()) {
              updateData.company_name = proceso.company_name.trim();
            }
            result = await procesosService.updatePackaging(productionProcessId, updateData);
            break;

          case 'ENCAPSULATION':
            updateData = {
              net_weight_per_capsule: parseFloat(proceso.net_weight_per_capsule) || 0,
              actual_weight_per_capsule: parseFloat(proceso.actual_weight_per_capsule) || 0,
              bottles_produced: parseInt(proceso.bottles_produced) || 0
            };
            result = await procesosService.updateEncapsulation(productionProcessId, updateData);
            break;

          case 'CLEANING':
            updateData = {
              deep_cleaning: proceso.deep_cleaning || false,
              weekly_cleaning: proceso.weekly_cleaning || false,
              production_area: proceso.production_area || false,
              warehouse_area: proceso.warehouse_area || false,
              outside_area: proceso.outside_area || false,
              bathroom_area: proceso.bathroom_area || false,
              cleaner_name: proceso.cleaner_name || ''
            };
            result = await procesosService.updateCleaning(productionProcessId, updateData);
            break;

          case 'RECEIVING':
            updateData = {
              received_boxes: parseInt(proceso.received_boxes) || 0,
              weight_per_box: parseFloat(proceso.weight_per_box) || 0,
              total_received: parseFloat(proceso.total_received) || 0,
              supplier_name: proceso.supplier_name || '',
              received_by_name: proceso.received_by_name || ''
            };
            result = await procesosService.updateReceiving(productionProcessId, updateData);
            break;

          case 'RETURNS':
            updateData = {
              returned_quantity: parseInt(proceso.returned_quantity) || 0,
              client_name: proceso.client_name || '',
              return_status: proceso.return_status || 'GOOD'
            };
            result = await procesosService.updateReturns(productionProcessId, updateData);
            break;

          case 'MAINTENANCE':
            updateData = {
              equipment_name: proceso.equipment_name || '',
              maintenance_type: 'PREVENTIVE',
              technician_name: proceso.technician_name || '',
              parts_replaced: 'N/A',
              total_cost: 0,
              is_operational: true,
              description: proceso.description || ''
            };
            result = await procesosService.updateMaintenance(productionProcessId, updateData);
            break;

          default:
            // Para tipos desconocidos, usar el método genérico
            result = await procesosService.updateProceso(productionProcessId, proceso);
        }
      }

      if (result.success) {
        loadProcesos();
        setEditingId(null);
      }
    } catch (err) {
      console.error('Error saving:', err);
    }
  };

  const handleDownloadReport = () => {
    console.log('Descargar reporte de', processType);
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando registros..." />;
  }

  return (
    <div className="procesos-list-page">
      {/* Header */}
      <div className="procesos-list-header">
        <div>
          <h1 className="procesos-list-title">
            {getPageTitle().split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < getPageTitle().split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </h1>
        </div>
        <div className="procesos-list-actions">
          <button
            className="btn btn--outline"
            onClick={() => navigate('/system/procesos')}
          >
            REGRESAR AL MENÚ
          </button>
          <button
            className="btn btn--primary"
            onClick={handleDownloadReport}
          >
            <FiFileText size={18} />
            REPORTE
          </button>
        </div>
      </div>

      {/* Notas Generales */}
      <div className="general-notes-section">
        <label className="general-notes-label">NOTAS GENERALES:</label>
        <textarea
          className="general-notes-textarea"
          value={generalNotes}
          onChange={(e) => setGeneralNotes(e.target.value)}
          placeholder="Escribe notas generales sobre este proceso..."
          rows="4"
        />
      </div>

      {/* Tabla según tipo de proceso */}
      <div className="procesos-table-container">
        {renderTableByType(processType, procesos, newRow, editingId, handleEdit, handleSave, handleAddNew)}
      </div>
    </div>
  );
};

// Función auxiliar para crear fila vacía según tipo
const createEmptyRow = (type) => {
  const baseRow = {
    id: 'new',
    product_name: '',
    process_date: new Date().toISOString().split('T')[0],
    operator_name: ''
  };

  switch (type) {
    case 'ENCAPSULATION':
      return {
        ...baseRow,
        net_weight_per_capsule: 0,
        actual_weight_per_capsule: 0,
        bottles_produced: 0
      };
    case 'TEA_PACKING':
    case 'PACKAGING':
      return {
        ...baseRow,
        packed_units: 0,
        batch_number: '',
        company_name: '',
        packer_name: ''
      };
    case 'CLEANING':
      return {
        ...baseRow,
        deep_cleaning: false,
        weekly_cleaning: false,
        production_area: false,
        warehouse_area: false,
        outside_area: false,
        bathroom_area: false,
        cleaner_name: ''
      };
    case 'RECEIVING':
      return {
        ...baseRow,
        received_boxes: 0,
        weight_per_box: 0,
        total_received: 0,
        supplier_name: '',
        received_by_name: ''
      };
    case 'RETURNS':
      return {
        ...baseRow,
        returned_quantity: 0,
        client_name: '',
        return_status: 'GOOD'
      };
    case 'MAINTENANCE':
      return {
        ...baseRow,
        equipment_name: '',
        technician_name: '',
        maintenance_type: 'PREVENTIVE',
        parts_replaced: 'N/A',
        total_cost: 0,
        is_operational: true,
        description: ''
      };
    default:
      return baseRow;
  }
};

// Renderizar tabla según tipo de proceso
const renderTableByType = (type, procesos, newRow, editingId, handleEdit, handleSave, handleAddNew) => {
  const allRows = newRow ? [newRow, ...procesos] : procesos;

  switch (type) {
    case 'ENCAPSULATION':
      return <EncapsulationTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    case 'TEA_PACKING':
      return <TeaPackingTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    case 'PACKAGING':
      return <PackagingTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    case 'CLEANING':
      return <CleaningTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    case 'RECEIVING':
      return <ReceivingTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    case 'RETURNS':
      return <ReturnsTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    case 'MAINTENANCE':
      return <MaintenanceTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    default:
      return <div>Tabla no implementada para tipo: {type}</div>;
  }
};

// Componente de tabla para Encapsulado
const EncapsulationTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>NOMBRE DEL<br/>PRODUCTO</th>
          <th>FECHA DE<br/>PRODUCCIÓN</th>
          <th>PESO NETO<br/>POR CÁPSULA</th>
          <th>PESO POR<br/>CÁPSULA</th>
          <th>CANTIDAD PRODUCIDA<br/>EN BOTELLAS</th>
          <th>NOMBRE DEL<br/>OPERADOR</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const productName = row.production_process?.product_name || row.product_name || '';
          const processDate = row.production_process?.process_date || row.process_date || '';
          const operatorName = row.production_process?.operator_name || row.operator_name || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  productName || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? new Date(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.net_weight_per_capsule || ''}
                    onChange={(e) => handleFieldChange('net_weight_per_capsule', e.target.value)}
                    placeholder="0 MCG"
                  />
                ) : (
                  `${row.net_weight_per_capsule || 0} MCG`
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.actual_weight_per_capsule || ''}
                    onChange={(e) => handleFieldChange('actual_weight_per_capsule', e.target.value)}
                    placeholder="0 MCG"
                  />
                ) : (
                  `${row.actual_weight_per_capsule || 0} MCG`
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.bottles_produced || ''}
                    onChange={(e) => handleFieldChange('bottles_produced', e.target.value)}
                    placeholder="250"
                  />
                ) : (
                  row.bottles_produced || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.operator_name || ''}
                    onChange={(e) => handleFieldChange('operator_name', e.target.value)}
                  />
                ) : (
                  operatorName || '-'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Tés (TEA_PACKING)
const TeaPackingTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>NOMBRE DEL<br/>PRODUCTO</th>
          <th>FECHA DE<br/>EMPAQUETADO</th>
          <th>UNIDADES<br/>EMPAQUETADAS</th>
          <th>NÚMERO DE<br/>LOTE</th>
          <th>NOMBRE DE<br/>COMPAÑÍA</th>
          <th>NOMBRE DEL<br/>EMPACADOR</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const productName = row.production_process?.product_name || row.product_name || '';
          const processDate = row.production_process?.process_date || row.process_date || '';
          const batchNumber = row.production_process?.batch_number || row.batch_number || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  productName || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? new Date(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.packed_units || ''}
                    onChange={(e) => handleFieldChange('packed_units', e.target.value)}
                  />
                ) : (
                  row.packed_units || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.batch_number || ''}
                    onChange={(e) => handleFieldChange('batch_number', e.target.value)}
                    placeholder="0000"
                  />
                ) : (
                  batchNumber || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.company_name || ''}
                    onChange={(e) => handleFieldChange('company_name', e.target.value)}
                  />
                ) : (
                  row.company_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.packer_name || ''}
                    onChange={(e) => handleFieldChange('packer_name', e.target.value)}
                  />
                ) : (
                  row.packer_name || '-'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Empacado (PACKAGING)
const PackagingTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>NOMBRE DEL<br/>PRODUCTO</th>
          <th>FECHA DE<br/>EMPAQUETADO</th>
          <th>UNIDADES<br/>EMPAQUETADAS</th>
          <th>NÚMERO DE<br/>LOTE</th>
          <th>NOMBRE DE<br/>COMPAÑÍA</th>
          <th>NOMBRE DEL<br/>EMPACADOR</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const productName = row.production_process?.product_name || row.product_name || '';
          const processDate = row.production_process?.process_date || row.process_date || '';
          const batchNumber = row.production_process?.batch_number || row.batch_number || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  productName || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? new Date(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.packed_units || ''}
                    onChange={(e) => handleFieldChange('packed_units', e.target.value)}
                  />
                ) : (
                  row.packed_units || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.batch_number || ''}
                    onChange={(e) => handleFieldChange('batch_number', e.target.value)}
                    placeholder="0000"
                  />
                ) : (
                  batchNumber || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.company_name || ''}
                    onChange={(e) => handleFieldChange('company_name', e.target.value)}
                  />
                ) : (
                  row.company_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.packer_name || ''}
                    onChange={(e) => handleFieldChange('packer_name', e.target.value)}
                  />
                ) : (
                  row.packer_name || '-'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Limpieza (CLEANING) - Con checkboxes
const CleaningTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table proceso-table--cleaning">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>FECHA</th>
          <th>LIMPIEZA<br/>PROFUNDA</th>
          <th>LIMPIEZA<br/>SEMANAL</th>
          <th colSpan="4" className="areas-header">ÁREAS</th>
          <th>NOMBRE</th>
          <th className="actions-column"></th>
        </tr>
        <tr className="sub-header">
          <th></th>
          <th></th>
          <th></th>
          <th></th>
          <th className="area-column">PRODUCCIÓN</th>
          <th className="area-column">ALMACÉN</th>
          <th className="area-column">EXTERIOR</th>
          <th className="area-column">BAÑO</th>
          <th></th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const processDate = row.production_process?.process_date || row.process_date || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? new Date(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.deep_cleaning || false}
                    onChange={(e) => handleCheckboxChange('deep_cleaning', e.target.checked)}
                  />
                ) : (
                  currentRow.deep_cleaning ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.weekly_cleaning || false}
                    onChange={(e) => handleCheckboxChange('weekly_cleaning', e.target.checked)}
                  />
                ) : (
                  currentRow.weekly_cleaning ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.production_area || false}
                    onChange={(e) => handleCheckboxChange('production_area', e.target.checked)}
                  />
                ) : (
                  currentRow.production_area ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.warehouse_area || false}
                    onChange={(e) => handleCheckboxChange('warehouse_area', e.target.checked)}
                  />
                ) : (
                  currentRow.warehouse_area ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.outside_area || false}
                    onChange={(e) => handleCheckboxChange('outside_area', e.target.checked)}
                  />
                ) : (
                  currentRow.outside_area ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.bathroom_area || currentRow.restroom || false}
                    onChange={(e) => handleCheckboxChange('bathroom_area', e.target.checked)}
                  />
                ) : (
                  (currentRow.bathroom_area || currentRow.restroom) ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.cleaner_name || currentRow.operator_name || ''}
                    onChange={(e) => {
                      handleFieldChange('cleaner_name', e.target.value);
                      handleFieldChange('operator_name', e.target.value);
                    }}
                  />
                ) : (
                  row.cleaner_name || row.operator_name || '-'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Recepción (RECEIVING)
const ReceivingTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>NOMBRE DEL<br/>PRODUCTO</th>
          <th>FECHA</th>
          <th>CAJAS<br/>RECIBIDAS</th>
          <th>PESO POR<br/>CAJA</th>
          <th>TOTAL<br/>RECIBIDO</th>
          <th>COMPAÑÍA</th>
          <th>NOMBRE DEL<br/>EMPACADOR</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const productName = row.production_process?.product_name || row.product_name || '';
          const processDate = row.production_process?.process_date || row.process_date || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  productName || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? new Date(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.received_boxes || ''}
                    onChange={(e) => handleFieldChange('received_boxes', e.target.value)}
                  />
                ) : (
                  row.received_boxes || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.weight_per_box || ''}
                    onChange={(e) => handleFieldChange('weight_per_box', e.target.value)}
                  />
                ) : (
                  row.weight_per_box || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.total_received || ''}
                    onChange={(e) => handleFieldChange('total_received', e.target.value)}
                  />
                ) : (
                  row.total_received || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.supplier_name || currentRow.company_name || ''}
                    onChange={(e) => handleFieldChange('supplier_name', e.target.value)}
                  />
                ) : (
                  row.supplier_name || row.company_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.received_by_name || currentRow.receiver_name || ''}
                    onChange={(e) => handleFieldChange('received_by_name', e.target.value)}
                  />
                ) : (
                  row.received_by_name || row.receiver_name || '-'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Retornos (RETURNS)
const ReturnsTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>NOMBRE DEL<br/>PRODUCTO</th>
          <th>FECHA</th>
          <th>CANTIDAD<br/>RETORNADA</th>
          <th>NOMBRE DEL<br/>CLIENTE</th>
          <th>ESTADO DEL PRODUCTO<br/>(MALO O BUENO)</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const productName = row.production_process?.product_name || row.product_name || '';
          const processDate = row.production_process?.process_date || row.process_date || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  productName || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? new Date(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.returned_quantity || currentRow.quantity_returned || ''}
                    onChange={(e) => handleFieldChange('returned_quantity', e.target.value)}
                  />
                ) : (
                  row.returned_quantity || row.quantity_returned || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.client_name || ''}
                    onChange={(e) => handleFieldChange('client_name', e.target.value)}
                  />
                ) : (
                  row.client_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <select
                    className="inline-input"
                    value={currentRow.return_status || 'GOOD'}
                    onChange={(e) => handleFieldChange('return_status', e.target.value)}
                  >
                    <option value="GOOD">BUENO</option>
                    <option value="BAD">MALO</option>
                  </select>
                ) : (
                  row.return_status === 'GOOD' ? 'BUENO' : row.return_status === 'BAD' ? 'MALO' : row.return_status || 'BUENO'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Mantenimiento (MAINTENANCE)
const MaintenanceTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>NOMBRE DEL<br/>EQUIPO</th>
          <th>FECHA</th>
          <th>NOMBRE DEL<br/>TÉCNICO</th>
          <th>NOTA</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          // Acceder a los datos anidados correctamente
          const processDate = row.production_process?.process_date || row.process_date || '';

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.equipment_name || ''}
                    onChange={(e) => handleFieldChange('equipment_name', e.target.value)}
                  />
                ) : (
                  row.equipment_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  processDate ? new Date(processDate).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.technician_name || ''}
                    onChange={(e) => handleFieldChange('technician_name', e.target.value)}
                  />
                ) : (
                  row.technician_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.description || currentRow.notes || ''}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder="Agregar nota..."
                  />
                ) : (
                  row.description || row.notes || '-'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default ProcesosList;
